import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Phone, Mail, Facebook } from "lucide-react";

const TERMS = {
  title: "Terms of Service",
  intro:
    "These terms explain how this website works and what to expect when you request a free estimate. By using this site, you agree to these terms.",
  sections: [
    {
      h: "Estimates",
      p: "All estimates requested through this website are provided free of charge and carry no obligation. Submission of the estimate form does not constitute a contract for work.",
    },
    {
      h: "Information",
      p: "Information submitted through the estimate form — including name, address, phone number, and project details — is used solely to respond to your request. It is never sold or shared with third parties, and you may request its deletion at any time.",
    },
    {
      h: "Content",
      p: "All content on this website, including photographs of completed work, text, and branding, is the property of Trumbull Paving Co. and may not be reproduced or used without written permission.",
    },
  ],
};

const FACEBOOK_URL = "https://www.facebook.com/share/19RzXrKSQL/?mibextid=wwXIfr";

export default function Legal() {
  // keep legal pages out of search results (accessible only via the website's footer links)
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    meta.dataset.legalNoindex = "1";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  return (
    <div className="min-h-screen bg-white text-royal-dark" data-testid="terms-page">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-royal-dark/10 bg-white">
        <div className="relative flex h-16 w-full items-center px-6">
          <Link to="/" aria-label="Back to home" data-testid="legal-logo-link">
            <img src="/tcc-logo.png" alt="Trumbull Paving Co. logo" className="h-10 w-auto" />
          </Link>
          <span className="absolute left-1/2 top-1/2 block -translate-x-1/2 -translate-y-1/2 text-center leading-none">
            <span className="block font-display text-xl font-bold uppercase tracking-tight text-black">
              Trumbull
            </span>
            <span className="mt-0.5 flex items-center justify-center gap-1 font-display text-[10px] font-semibold uppercase tracking-[0.12em] text-royal-dark">
              <span className="h-px w-3 bg-royal-dark/80" />
              Paving Co.
              <span className="h-px w-3 bg-royal-dark/80" />
            </span>
          </span>
        </div>
      </header>
      <main className="mx-auto max-w-2xl px-6 pb-16 pt-[81px]">
        <Link
          to="/"
          data-testid="legal-back-link"
          className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] text-royal-dark/70 transition-colors duration-200 hover:text-royal-dark"
        >
          <span className="font-display text-sm font-light leading-none">&lt;</span>
          Back to home
        </Link>

        <div className="mt-8 text-center">
          <h1 className="font-display text-3xl font-bold tracking-tight text-royal-dark sm:text-4xl">
            {TERMS.title}
          </h1>
          <p className="mx-auto mt-6 max-w-xl border-l-2 border-royal-dark/30 pl-4 text-left text-sm leading-relaxed text-royal-dark/85 sm:text-base">
            {TERMS.intro}
          </p>
        </div>

        <div className="mt-10 divide-y divide-royal-dark/10 border-y border-royal-dark/10">
          {TERMS.sections.map((s, i) => (
            <section key={s.h} className="flex gap-5 py-6">
              <span className="font-display text-sm font-semibold text-royal-dark/40">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <h2 className="font-display text-lg font-bold text-royal-dark">{s.h}</h2>
                <p className="mt-2 text-sm leading-relaxed text-royal-dark/85 sm:text-base">{s.p}</p>
              </div>
            </section>
          ))}
        </div>

        <div className="mt-10 border border-royal-dark/25 bg-white p-6 shadow-[6px_6px_0_0_#003399]">
          <h2 className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-royal-dark">
            Questions
          </h2>
          <div className="mt-4 space-y-2 text-sm font-medium text-royal-dark sm:text-base">
            <p className="flex items-center gap-3">
              <Mail className="h-4 w-4 shrink-0" />
              <a href="mailto:trumbullcont@aol.com" className="underline underline-offset-4 transition-colors duration-200 hover:text-royal-dark/60">
                trumbullcont@aol.com
              </a>
            </p>
            <p className="flex items-center gap-3">
              <Phone className="h-4 w-4 shrink-0" />
              <a href="tel:+13306372551" className="transition-colors duration-200 hover:text-royal-dark/60">
                330-637-2551
              </a>
            </p>
            <p className="flex items-center gap-3">
              <Facebook className="h-4 w-4 shrink-0" />
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 transition-colors duration-200 hover:text-royal-dark/60">
                Trumbull Paving Co.
              </a>
            </p>
          </div>
        </div>

        <p className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-royal-dark/50">
          © 1984–2026 Trumbull Paving Co. — Trumbull County, Ohio
        </p>
      </main>
    </div>
  );
}
